<ng-container *ngIf="challenge$ | async as challenge">
  <div class="auth-heading">
    <h1 class="auth-heading__title">{{ 'authentication.mfa.title' | translate }}</h1>
    <p class="auth-heading__subtitle">
      {{
        (mode === 'recovery'
          ? 'authentication.mfa.recovery_subtitle'
          : 'authentication.mfa.subtitle'
        ) | translate
      }}
    </p>
  </div>

  <form
    *ngIf="mode === 'code'"
    class="auth-form"
    [formGroup]="codeForm"
    (ngSubmit)="submit()"
    (keydown.enter)="onEnterKey($event)"
  >
    <!-- One field, not six boxes: segmented inputs break paste, autofill,
         RTL and screen readers. -->
    <ui-form-field
      [id]="'mfa-code'"
      testId="mfa-code"
      type="text"
      formControlName="code"
      autocomplete="one-time-code"
      inputMode="numeric"
      [maxLength]="6"
      [label]="'authentication.mfa.code' | translate"
      [placeholder]="'authentication.mfa.code_placeholder' | translate"
      [errorMessages]="{
        required: 'authentication.mfa.required_code' | translate,
        pattern: 'authentication.mfa.invalid_code_format' | translate
      }"
      [fullWidth]="true"
    ></ui-form-field>

    <p class="mfa-challenge__hint" *ngIf="maskedEmail$ | async as maskedEmail">
      {{ 'authentication.mfa.code_sent_to' | translate: { email: maskedEmail } }}
    </p>

    <div class="auth-form__actions">
      <!-- ui-button renders a typeless <button>, which submits by default. -->
      <ui-button
        type="primary"
        testId="mfa-submit"
        [label]="'authentication.mfa.verify' | translate"
        [disabled]="(loading$ | async) === true"
        [isLoading]="(loading$ | async) === true"
        [fullWidth]="true"
      ></ui-button>
    </div>
  </form>

  <!-- A sibling form, not a second field above: a recovery code is longer,
       alphanumeric, and must never auto-submit. -->
  <form
    *ngIf="mode === 'recovery'"
    class="auth-form"
    [formGroup]="recoveryForm"
    (ngSubmit)="submitRecovery()"
    (keydown.enter)="onEnterRecovery($event)"
  >
    <!-- autocomplete="off", not one-time-code: that heuristic offers
         six-digit SMS codes. Upper-casing is done in the component, since
         ui-form-field has no autocapitalize input. -->
    <ui-form-field
      [id]="'mfa-recovery-code'"
      testId="mfa-recovery-code"
      type="text"
      formControlName="recoveryCode"
      autocomplete="off"
      inputMode="text"
      [maxLength]="recoveryMaxInput"
      [label]="'authentication.mfa.recovery_code' | translate"
      [placeholder]="'authentication.mfa.recovery_code_placeholder' | translate"
      [errorMessages]="{
        required: 'authentication.mfa.required_recovery_code' | translate,
        pattern: 'authentication.mfa.invalid_recovery_code_format' | translate
      }"
      [fullWidth]="true"
    ></ui-form-field>

    <div class="auth-form__actions">
      <ui-button
        type="primary"
        testId="mfa-recovery-submit"
        [label]="'authentication.mfa.verify' | translate"
        [disabled]="(loading$ | async) === true"
        [isLoading]="(loading$ | async) === true"
        [fullWidth]="true"
      ></ui-button>
    </div>
  </form>

  <!-- Outside both forms, shared by both modes. type="button" so none of
       them submits either one. -->
  <div class="auth-form__actions">
    <button
      *ngIf="mode === 'code' && offersEmail(challenge)"
      class="auth-link mfa-challenge__link"
      type="button"
      data-testid="mfa-send-email"
      [disabled]="resendWait > 0 || emailCodeSending"
      (click)="sendEmailCode()"
    >
      <ng-container *ngIf="resendWait > 0; else sendNow">
        {{ 'authentication.mfa.resend_in' | translate: { seconds: resendWait } }}
      </ng-container>
      <ng-template #sendNow>
        {{ 'authentication.mfa.send_code_by_email' | translate }}
      </ng-template>
    </button>

    <!-- Always offered, not gated on the challenge advertising recovery
         codes: gating would tell anyone holding the password whether a sheet
         exists, and a user with none gets the usual generic refusal. -->
    <button
      *ngIf="mode === 'code'"
      class="auth-link mfa-challenge__link"
      type="button"
      data-testid="mfa-use-recovery-code"
      (click)="useRecoveryCode()"
    >
      {{ 'authentication.mfa.use_recovery_code' | translate }}
    </button>

    <button
      *ngIf="mode === 'recovery'"
      class="auth-link mfa-challenge__link"
      type="button"
      data-testid="mfa-use-authenticator"
      (click)="useAuthenticatorCode()"
    >
      {{ 'authentication.mfa.use_authenticator_instead' | translate }}
    </button>

    <button
      class="auth-link mfa-challenge__link"
      type="button"
      data-testid="mfa-use-another-account"
      (click)="useAnotherAccount()"
    >
      {{ 'authentication.mfa.use_another_account' | translate }}
    </button>
  </div>
</ng-container>
